import {neverland as $, html, useState, useEffect} from 'neverland';
import { isAuthenticated, onAuthChange, getCurrentUser, getCredentials, signOut, getSession, getUserEmail, getUserGroups, getCurrentRole, isAdmin, isResearcher, isUser } from '../lib/cognito.js';
import database from '../lib/database.js';

import collections from '../specs/collections.js';
import Router from './Router.js'

const App = $(function() {
	
	let publicPage = (new URLSearchParams(window.location.search)).get('token') !== null ? 'ResetPassword' : null;
	let page = (new URLSearchParams(window.location.search)).get('p');

	const [auth, setAuth] = useState({
		isLoggedIn: false,
		currentUser: null,
		db: database,
		publicPage: publicPage,
		page: page,
		userGroups: [],
		currentRole: null,
		isAdmin: false,
		isResearcher: false,
		isUser: false
	});

	let lastActivity = Date.now();
	let sessionCheckInterval = null;

	const updateActivity = () => {
		lastActivity = Date.now();
	};

	const checkSession = async () => {
		if (!auth.isLoggedIn) return;
		
		const now = Date.now();
		const timeSinceActivity = now - lastActivity;
		
		if (timeSinceActivity > 30 * 60 * 1000) { // 30 minutes
			// Auto logout after 30 minutes of inactivity
			signOut();
			localStorage.removeItem('userEmail');
			setAuth(prev => ({ ...prev, isLoggedIn: false, currentUser: null, userEmail: null }));
		} else {
			// Refresh token if session is still active
			try {
				await getSession();
				await getCredentials();
			} catch (err) {
				console.error('Session refresh failed:', err);
				signOut();
				localStorage.removeItem('userEmail');
				setAuth(prev => ({ ...prev, isLoggedIn: false, currentUser: null, userEmail: null }));
			}
		}
	};
	
	useEffect(() => {
		isAuthenticated().then(async (loggedIn) => {
			if (loggedIn) {
				try {
					const credentials = await getCredentials();
					AWS.config.credentials = credentials;
					
					// Get cached userEmail as fallback
					const cachedUserEmail = localStorage.getItem('userEmail');
					
					// Get user groups and role information
					const userGroups = getUserGroups();
					const currentRole = getCurrentRole();
					
					setAuth(prev => ({ 
						...prev, 
						isLoggedIn: loggedIn, 
						currentUser: getCurrentUser(), 
						userEmail: cachedUserEmail,
						userGroups: userGroups,
						currentRole: currentRole,
						isAdmin: isAdmin(),
						isResearcher: isResearcher(),
						isUser: isUser()
					}));
					
					// Try to get email from Cognito and update if successful
					const currentUser = getCurrentUser();
					try {
						const cognitoEmail = await getUserEmail(currentUser);
						if (cognitoEmail) {
							setAuth(prev => ({ ...prev, userEmail: cognitoEmail }));
						}
					} catch (emailErr) {
						console.warn('Could not get user email from Cognito, using cached email');
					}
				} catch (err) {
					console.error('Failed to set AWS credentials:', err);
					setAuth(prev => ({ ...prev, isLoggedIn: loggedIn, currentUser: getCurrentUser() }));
				}
			} else {
				AWS.config.credentials = null;
				localStorage.removeItem('userEmail');
				setAuth(prev => ({ ...prev, isLoggedIn: loggedIn, currentUser: getCurrentUser(), userEmail: null }));
			}
		});
		
		onAuthChange(async (user) => {
			if (user) {
				try {
					const credentials = await getCredentials();
					AWS.config.credentials = credentials;
					
					// Get user groups and role information
					const userGroups = getUserGroups();
					const currentRole = getCurrentRole();
					
					setAuth(prev => ({ 
						...prev, 
						isLoggedIn: !!user, 
						currentUser: user,
						userGroups: userGroups,
						currentRole: currentRole,
						isAdmin: isAdmin(),
						isResearcher: isResearcher(),
						isUser: isUser()
					}));
				} catch (err) {
					console.error('Failed to set AWS credentials:', err);
					setAuth(prev => ({ ...prev, isLoggedIn: !!user, currentUser: user }));
				}
			} else {
				AWS.config.credentials = null;
				setAuth(prev => ({ 
					...prev, 
					isLoggedIn: !!user, 
					currentUser: user,
					userGroups: [],
					currentRole: null,
					isAdmin: false,
					isResearcher: false,
					isUser: false
				}));
			}
		});

		const handlePopState = () => {
			const newPage = (new URLSearchParams(window.location.search)).get('p');
			setAuth(prev => ({ ...prev, page: newPage }));
		};

		window.addEventListener('popstate', handlePopState);
		
		// Add activity listeners
		const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
		activityEvents.forEach(event => {
			document.addEventListener(event, updateActivity, true);
		});
		
		// Start session check interval
		sessionCheckInterval = setInterval(checkSession, 60000); // Check every minute
		
		return () => {
			window.removeEventListener('popstate', handlePopState);
			activityEvents.forEach(event => {
				document.removeEventListener(event, updateActivity, true);
			});
			if (sessionCheckInterval) clearInterval(sessionCheckInterval);
		};
	}, []);
	
	return html`
		${Router({auth, setAuth})}
	`;

});

export default App;